// utils/roadSnap.js - COMPLETE REWRITE WITH PROPER CACHING

// ✅ Small, safe, auto-cleaning cache
const SNAP_CACHE = new Map();
const CACHE_TTL = 60 * 1000; // 60 seconds (increased for better caching)
const MAX_CACHE_SIZE = 1000; // Increased cache size
const CLEANUP_INTERVAL = 5 * 60 * 1000; // Cleanup every 5 minutes

// 🔹 Cache cleanup timer
let cleanupTimer = null;

// 🔹 Helper: Generate cache key
function getCacheKey(points) {
  return points
    .map(p => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`)
    .join('|');
}

// 🔹 Cleanup old entries
function cleanupCache() {
  const now = Date.now();
  let deletedCount = 0;

  for (const [key, val] of SNAP_CACHE.entries()) {
    if (now - val.ts > CACHE_TTL) {
      SNAP_CACHE.delete(key);
      deletedCount++;
    }
  }

  // If cache is still too large, delete oldest entries
  if (SNAP_CACHE.size > MAX_CACHE_SIZE) {
    const entries = Array.from(SNAP_CACHE.entries())
      .sort((a, b) => a[1].ts - b[1].ts);
    
    const toDelete = entries.slice(0, SNAP_CACHE.size - MAX_CACHE_SIZE);
    toDelete.forEach(([key]) => SNAP_CACHE.delete(key));
    deletedCount += toDelete.length;
  }

  if (deletedCount > 0) {
    console.log(`🧹 Cleaned ${deletedCount} cached road snap entries`);
  }
}

// 🔹 Start periodic cleanup
function startCleanupTimer() {
  if (cleanupTimer) return;
  
  cleanupTimer = setInterval(() => {
    cleanupCache();
  }, CLEANUP_INTERVAL);
}

// 🔹 Stop cleanup timer (useful for cleanup on unmount)
export function stopRoadSnapCache() {
  if (cleanupTimer) {
    clearInterval(cleanupTimer);
    cleanupTimer = null;
  }
  SNAP_CACHE.clear();
}

/**
 * 🛣️ Snap GPS points to nearest roads using Google Roads API
 * @param {Array<{lat: number, lng: number}>} points - Array of GPS coordinates
 * @returns {Promise<Array<{lat: number, lng: number}>>} - Road-snapped coordinates
 */
export async function snapToRoad(points = []) {
  // Validate input
  if (!Array.isArray(points) || points.length < 2) {
    console.warn('⚠️ snapToRoad: Need at least 2 points');
    return points;
  }

  // Validate points
  const validPoints = points.filter(p => {
    const isValid = 
      p && 
      typeof p.lat === 'number' && 
      typeof p.lng === 'number' &&
      Math.abs(p.lat) <= 90 && 
      Math.abs(p.lng) <= 180 &&
      p.lat !== 0 && 
      p.lng !== 0;
    
    if (!isValid) {
      console.warn('⚠️ Invalid point filtered:', p);
    }
    return isValid;
  });

  if (validPoints.length < 2) {
    console.warn('⚠️ Not enough valid points after filtering');
    return points;
  }

  // Check cache
  const key = getCacheKey(validPoints);
  const cached = SNAP_CACHE.get(key);

  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    console.log('✅ Using cached road snap');
    return cached.points;
  }

  // Start cleanup timer on first use
  startCleanupTimer();

  // Prepare path for API (max 100 points per request)
  const pointsToSnap = validPoints.slice(0, 100);
  const path = pointsToSnap
    .map(p => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`)
    .join('|');

  try {
    const url = `https://roads.googleapis.com/v1/snapToRoads` +
      `?path=${path}` +
      `&interpolate=true` +
      `&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.warn(`⚠️ Roads API error (${res.status}):`, errorText);
      return validPoints; // Fallback to original points
    }

    const json = await res.json();

    if (!json.snappedPoints || json.snappedPoints.length === 0) {
      console.warn('⚠️ Roads API returned no snapped points');
      return validPoints;
    }

    // Extract snapped coordinates
    const snapped = json.snappedPoints.map(p => ({
      lat: p.location.latitude,
      lng: p.location.longitude
    }));

    // Cache the result
    SNAP_CACHE.set(key, {
      ts: Date.now(),
      points: snapped
    });

    console.log(`✅ Snapped ${pointsToSnap.length} points to ${snapped.length} road points`);
    return snapped;

  } catch (err) {
    console.error('❌ Road snap failed:', err.message);
    return validPoints; // Safe fallback
  }
}

/**
 * 🛣️ Snap a playback/history route onto nearby roads while preserving
 * the original point count and metadata order.
 * @param {Array<object>} points
 * @returns {Promise<Array<object>>}
 */
export async function snapRouteToRoadVertices(points = []) {
  if (!Array.isArray(points) || points.length < 2) return Array.isArray(points) ? points : [];

  const normalized = points
    .map((point, index) => {
      const lat = Number(point?.lat ?? point?.latitude);
      const lng = Number(point?.lng ?? point?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        index,
        point,
        lat,
        lng,
      };
    })
    .filter(Boolean);

  if (normalized.length < 2) return points;

  const snappedByIndex = new Map();
  const chunkSize = 100;
  const step = chunkSize - 1;

  for (let start = 0; start < normalized.length; start += step) {
    const chunk = normalized.slice(start, start + chunkSize);
    if (chunk.length < 2) {
      chunk.forEach((entry) => {
        if (!snappedByIndex.has(entry.index)) {
          snappedByIndex.set(entry.index, { lat: entry.lat, lng: entry.lng });
        }
      });
      continue;
    }

    const path = chunk
      .map((entry) => `${entry.lat.toFixed(6)},${entry.lng.toFixed(6)}`)
      .join("|");

    try {
      const url = `https://roads.googleapis.com/v1/snapToRoads` +
        `?path=${path}` +
        `&interpolate=true` +
        `&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`Road snap failed with ${res.status}`);
      }

      const json = await res.json();
      const snappedPoints = Array.isArray(json?.snappedPoints) ? json.snappedPoints : [];

      snappedPoints.forEach((snapped) => {
        const originalIndex = Number(snapped?.originalIndex);
        if (!Number.isInteger(originalIndex) || originalIndex < 0 || originalIndex >= chunk.length) {
          return;
        }
        const source = chunk[originalIndex];
        const lat = Number(snapped?.location?.latitude);
        const lng = Number(snapped?.location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        snappedByIndex.set(source.index, { lat, lng });
      });
    } catch (err) {
      console.warn("⚠️ snapRouteToRoadVertices chunk failed:", err?.message || err);
      chunk.forEach((entry) => {
        if (!snappedByIndex.has(entry.index)) {
          snappedByIndex.set(entry.index, { lat: entry.lat, lng: entry.lng });
        }
      });
    }
  }

  return points.map((point, index) => {
    const snapped = snappedByIndex.get(index);
    if (!snapped) return point;
    return {
      ...point,
      lat: snapped.lat,
      lng: snapped.lng,
      roadSnapped: true,
    };
  });
}

/**
 * 🧭 Calculate bearing between two GPS points
 * @param {Object} from - {lat, lng}
 * @param {Object} to - {lat, lng}
 * @returns {number} - Bearing in degrees (0-360)
 */
export function calculateBearing(from, to) {
  if (!from || !to) return 0;

  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) -
            Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  let bearing = Math.atan2(y, x) * (180 / Math.PI);
  bearing = (bearing + 360) % 360; // Normalize to 0-360

  return bearing;
}

/**
 * 📏 Calculate distance between two GPS points (Haversine formula)
 * @param {Object} from - {lat, lng}
 * @param {Object} to - {lat, lng}
 * @returns {number} - Distance in meters
 */
export function calculateDistance(from, to) {
  if (!from || !to) return 0;

  const R = 6371000; // Earth radius in meters
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLat = ((to.lat - from.lat) * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
